import { FC } from 'react'
import { useTranslation } from 'react-i18next'

import CategoryRoundedIcon from '@mui/icons-material/CategoryRounded'
import CompareArrowsIcon from '@mui/icons-material/CompareArrows'
import KeyboardArrowRightRoundedIcon from '@mui/icons-material/KeyboardArrowRightRounded'
import SportsEsportsRoundedIcon from '@mui/icons-material/SportsEsportsRounded'
import { alpha, Box, Button, ButtonBase, Divider, Popover, Typography, useTheme } from '@mui/material'

import { borderRadiusDefault } from '../../../../themes/theme'
import { useUAInsightsMeta } from '../../hooks/useUAInsightsMeta'
import { useUATaxonomy } from '../../hooks/useUATaxonomy'

interface CompareTargetPickerProps {
  /** Anchor element the popover is positioned against — typically the Compare button. */
  anchorEl: HTMLElement | null
  open: boolean
  onClose: () => void
  /** Invoked when the user picks the "Select Game" entry. Caller is responsible for closing the popover and opening the game-listing dialog. */
  onSelectGameClick: () => void
  /** Invoked when the user clicks a genre row. Caller closes the popover and opens the game-vs-genre comparison-result dialog. */
  onSelectGenre: (genreId: string) => void
}

/**
 * Lightweight popover anchored to the UA Insights Game Page Compare button. It
 * lets the user choose what to compare the current game against:
 *
 *   1. Another game — opens the existing `CompareGameDialog` listing (the
 *      caller calls `openCompareGame()` after closing the popover).
 *   2. A genre — short-circuits the listing step and jumps straight into the
 *      game-vs-genre comparison-result dialog (the caller calls
 *      `openCompareGenreResult(genreId)` after closing the popover).
 *
 * The genre list is sourced from `getAllGenres()` rather than the dashboard's
 * scoped list so every genre is always reachable; the *aggregate* the
 * comparison-result dialog renders is still scoped by the active
 * network/month filter via `useGenreCreativeAggregate`.
 */
export const CompareTargetPicker: FC<CompareTargetPickerProps> = ({ anchorEl, open, onClose, onSelectGameClick, onSelectGenre }) => {
  const { t } = useTranslation('ua-insights')
  const theme = useTheme()

  const { genres } = useUAInsightsMeta()
  const { genreLabel } = useUATaxonomy()

  const handleSelectGame = () => {
    onClose()
    onSelectGameClick()
  }

  const handleSelectGenre = (genreId: string) => {
    onClose()
    onSelectGenre(genreId)
  }

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      PaperProps={{
        sx: {
          mt: 1,
          width: 320,
          borderRadius: borderRadiusDefault,
          overflow: 'hidden',
        },
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ px: 2, pt: 1.5, pb: 1 }}>
          <Typography variant="overline" sx={{ fontWeight: 700, letterSpacing: '0.08em', color: 'text.secondary', lineHeight: 1.3 }}>
            {t('compare_picker_title')}
          </Typography>
        </Box>

        <Box sx={{ px: 2, pb: 1.5 }}>
          <Button
            fullWidth
            variant="contained"
            color="primary"
            startIcon={<CompareArrowsIcon />}
            onClick={handleSelectGame}
            sx={{ justifyContent: 'flex-start', fontWeight: 'bold' }}
          >
            {t('compare_picker_select_game')}
          </Button>
        </Box>

        <Divider />

        <Box sx={{ px: 2, pt: 1.25, pb: 0.5 }}>
          <Typography variant="overline" sx={{ fontWeight: 700, letterSpacing: '0.08em', color: 'text.secondary', lineHeight: 1.3 }}>
            {t('compare_picker_genre_section_label')}
          </Typography>
        </Box>

        <Box
          // Cap the visible height so a long genre catalogue scrolls inside
          // the popover instead of pushing the popover off screen.
          sx={{
            maxHeight: 320,
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            pb: 1,
          }}
        >
          {genres.length === 0 ? (
            <Typography variant="body2" sx={{ px: 2, py: 1.5, color: 'text.secondary', fontStyle: 'italic' }}>
              {t('compare_picker_no_genres')}
            </Typography>
          ) : (
            genres.map((genre) => (
              <ButtonBase
                key={genre.id}
                onClick={() => handleSelectGenre(genre.id)}
                focusRipple
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  textAlign: 'left',
                  px: 2,
                  py: 1,
                  gap: 1.25,
                  '&:hover': { backgroundColor: alpha(theme.palette.text.primary, 0.04) },
                  '&.Mui-focusVisible': { backgroundColor: alpha(theme.palette.primary.main, 0.08) },
                }}
              >
                <Box
                  aria-hidden
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    backgroundColor: alpha(theme.palette.primary.main, 0.12),
                    color: 'primary.main',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <CategoryRoundedIcon sx={{ fontSize: 18 }} />
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 'bold',
                      color: 'text.primary',
                      lineHeight: 1.25,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {genreLabel(genre)}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <SportsEsportsRoundedIcon sx={{ fontSize: 12, color: 'text.secondary' }} />
                    <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.3 }}>
                      {t('compare_picker_genre_games_count', { count: genre.gamesCount })}
                    </Typography>
                  </Box>
                </Box>
                <KeyboardArrowRightRoundedIcon sx={{ fontSize: 18, color: 'text.secondary', flexShrink: 0 }} />
              </ButtonBase>
            ))
          )}
        </Box>
      </Box>
    </Popover>
  )
}
